import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import type { AffiliatesHotelsCache } from "../../plans/PlanModel";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../core/BaseModuleDataModel";
import { LocationUI } from "./elements/LocationUI";
import LocationModuleModel from "./LocationModuleModel";

export type MapData = {
  lng: number;
  lat: number;
};

export type LocationModelCache = {
  affiliateHotelsDump?: AffiliatesHotelsCache;
};

export interface LocationModuleModelData extends BaseModuleModelData {
  label: string;
  description: string | null;
  userId: string;
  mapData: {
    lng: number;
    lat: number;
  } | null;
  cache: LocationModelCache;
}

export default class LocationModel extends BaseModuleDataModel<LocationModuleModelData> {
  constructor(stores: Stores, api: Api, data: LocationModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      cache: data.cache ?? {},
    });

    makeObservable(this);
  }

  @action async updateCache(newCache: AffiliatesHotelsCache) {
    this.data.cache.affiliateHotelsDump = newCache;

    this.api.modulesDataApi.set(this);
  }

  @action setMapData(mapData: { lat: number; lng: number }) {
    const oldMapData = this.data.mapData;

    if (oldMapData !== mapData) {
      this.data.mapData = mapData;

      this.updateTimestamp();
      this.api.modulesDataApi.set(this);

      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityUpdatedPositionInLocation,
        {
          location: this.data.label,
        },
      );
    }
  }

  @action async updateItem(data: {
    label: string;
    mapData: { lat: number; lng: number };
  }) {
    const oldMapData = this.data.mapData;
    const oldLabel = this.data.label;

    if (oldLabel !== data.label || oldMapData !== data.mapData) {
      this.data.mapData = data.mapData;
      this.data.label = data.label;

      this.updateTimestamp();
      await this.api.modulesDataApi.set(this);

      await this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityUpdatedLocation,
        {
          location: this.data.label,
        },
      );
    }
  }

  @action setDescription(newDescription: string) {
    const oldDescription = this.data.description;

    if (oldDescription !== newDescription) {
      this.data.description = newDescription;

      this.updateTimestamp();
      this.api.modulesDataApi.set(this);

      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityUpdatedDescriptionInLocation,
        {
          oldValue: oldDescription ?? "",
          newValue: newDescription,
          location: this.data.label,
        },
      );
    }
  }

  @computed get isInDoneResult(): boolean {
    if (!this.module.isDone) {
      return false;
    }

    if (LocationModuleModel.is(this.module)) {
      return !!this.module.data.selectedLocations?.find((s) => s === this.uid);
    }

    return false;
  }
  @action
  toFireStore(): Omit<LocationModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  @computed get user() {
    return this.plan.users.find((user) => user.uid === this.data.userId);
  }

  render() {
    return <LocationUI data={this} key={this.uid} />;
  }

  static is(
    // biome-ignore lint/suspicious/noExplicitAny: Because
    item: BaseModuleDataModel<any>,
  ): item is LocationModel {
    return item instanceof LocationModel;
  }
}
